import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {liveRoomReportSelector} from '../../store/selectors/analytics.selectors';
import {LeaderboardEntry, leaderboardTypes} from '../../incase-domain/analytics';
import LeaderboardDropdown from './LeaderboardDropdown';
import TabsComponent from '../../ui/components/common/TabsComponent';
import Button from '../../incase-domain/components/button/Button';

function Leaderboard(): JSX.Element | null {
    const {t} = useTranslation();
    const leaderboard = useSelector(liveRoomReportSelector);

    const [selectedLeaderboardType, setSelectedLeaderboardType] = useState<leaderboardTypes>('specificLeaderboard');
    const [userWithUpdatedScore, setUserWithUpdatedScore] = useState(-1);
    const previousEntriesRef = useRef<LeaderboardEntry[]>([]);

    const publicEntries = useMemo(() => leaderboard?.[selectedLeaderboardType] ?? [], [leaderboard, selectedLeaderboardType]);

    const personalEntry = useMemo(
        () => (selectedLeaderboardType === 'specificLeaderboard' ? leaderboard?.personalSpecificEntry : leaderboard?.personalGroupEntry),
        [leaderboard, selectedLeaderboardType]
    );

    const displayedList = useMemo(
        () => (personalEntry ? [...publicEntries, personalEntry] : publicEntries),
        [publicEntries, personalEntry]
    );

    useEffect(() => {
        const previousEntries = previousEntriesRef.current;
        previousEntriesRef.current = displayedList;
        setUserWithUpdatedScore(
            displayedList.findIndex((user) => user.score !== previousEntries.find((entry) => entry.id === user.id)?.score)
        );
    }, [displayedList]);

    useEffect(() => {
        if (selectedLeaderboardType === 'specificLeaderboard' && !leaderboard?.specificLeaderboard && leaderboard?.groupLeaderboard) {
            setSelectedLeaderboardType('groupLeaderboard');
        }
    }, [leaderboard, selectedLeaderboardType]);

    if (!leaderboard) {
        return null;
    }

    const bothLeaderboardsPresent = leaderboard.specificLeaderboard && leaderboard.groupLeaderboard;

    const getMedalIcon = (entry: LeaderboardEntry, index: number): JSX.Element => {
        const displayRank = entry.rank ?? index + 1;

        switch (displayRank - 1) {
            case 0:
                return <span className="c-leaderboard__item_order--withIcon">{'\u{1F947}'}</span>;
            case 1:
                return <span className="c-leaderboard__item_order--withIcon">{'\u{1F948}'}</span>;
            case 2:
                return <span className="c-leaderboard__item_order--withIcon">{'\u{1F949}'}</span>;
            default:
                return <span>{`${displayRank}.`}</span>;
        }
    };

    const leaderboardContent = (
        <div className="c-leaderboard">
            <div className="c-leaderboard__title">
                <Button variant="ghost" size="xl" leading="solar:cup-outline" padded={false} disablePointerEvents>
                    {t('leaderboard.rankings')}
                </Button>
            </div>
            {bothLeaderboardsPresent && (
                <TabsComponent
                    tabs={[
                        {id: 'specificLeaderboard', label: t('leaderboard.specific')},
                        {id: 'groupLeaderboard', label: t('leaderboard.group')},
                    ]}
                    activeTab={selectedLeaderboardType}
                    setActiveTab={(s) => setSelectedLeaderboardType(s as leaderboardTypes)}
                />
            )}

            <div className="c-leaderboard__list">
                {displayedList.length > 0 ? (
                    displayedList.map((user, i) => {
                        const isAppendedPersonalEntry = !!personalEntry && i === displayedList.length - 1;
                        const showDivider = (i === 3 && publicEntries.length > 3) || isAppendedPersonalEntry;

                        return (
                            <React.Fragment key={user.id}>
                                {showDivider && <hr />}
                                <div
                                    className={`c-leaderboard__item${userWithUpdatedScore === i ? ' c-leaderboard__item-flash' : ''}${
                                        user.isCurrentUser ? ' c-leaderboard__item--current' : ''
                                    }`}
                                >
                                    <div className="c-leaderboard__item_order">{getMedalIcon(user, i)}</div>
                                    <div className="c-leaderboard__item_name">{`${user.name || '_unknown_'}`}</div>
                                    <div className="c-leaderboard__item_score">{user.score}</div>
                                </div>
                            </React.Fragment>
                        );
                    })
                ) : (
                    <div className="c-leaderboard__item">{t('leaderboard.noUsers')}</div>
                )}
            </div>
        </div>
    );

    return (
        <LeaderboardDropdown trigger={<Button leading="solar:cup-outline" variant="ghost" size="xl" square />}>
            {leaderboardContent}
        </LeaderboardDropdown>
    );
}

export default Leaderboard;
